import { useState } from 'react'
import Layout, { Card } from '@/components/Layout'
import { Sheet, SheetPad, SheetRow, SheetDivider } from '@/components/Sheet'
import { Button, Field, fieldInputClass } from '@/components/ui'
import AttachmentPicker from '@/components/AttachmentPicker'
import Avatar from '@/components/Avatar'
import { router } from '@inertiajs/react'
import type { AccessLevel } from '@/types'
import { shrinkImage } from '@/lib/shrinkImage'
import { t } from '@/lib/i18n'

type ShareAccess = 'read' | 'claim' | 'write'
interface Share { user_id: number; email: string; access: ShareAccess }
interface PersonRow {
  id: number; name: string; avatar_url: string | null
  balance: number; total: number
  streak: number; goals_count: number; is_current: boolean
  timezone: string
  access: AccessLevel; is_owner: boolean; owner_email: string
  shares?: Share[]
}

// 时区决定这位成员的「今天」从几点开始，直接影响连续天数与热力图。挂在成员而非
// 账号上：共同抚养场景下两位家长可能不在同一时区，而归属日必须只有一个说法。
// 只列目标市场常用的几个 + 浏览器探测到的当前值，避免铺开 400 个 IANA 名字。
const COMMON_TIMEZONES = [
  'UTC',
  'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Taipei', 'Asia/Singapore', 'Asia/Tokyo',
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
  'America/Toronto', 'America/Vancouver',
  'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'Europe/Madrid',
  'Australia/Sydney', 'Australia/Perth', 'Pacific/Auckland',
]

function localTimeIn(timezone: string): string {
  try {
    // 用页面当前语言而不是写死 zh-CN：12/24 小时制这类习惯本来就该跟着语言走。
    // <html lang> 由 layout 按 I18n.locale 渲染，所以这里拿到的就是生效中的 locale。
    return new Date().toLocaleTimeString(document.documentElement.lang || undefined, {
      timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false,
    })
  } catch {
    return '—'
  }
}

function TimezonePicker({ person, canWrite }: { person: PersonRow; canWrite: boolean }) {
  const options = COMMON_TIMEZONES.includes(person.timezone)
    ? COMMON_TIMEZONES
    : [ person.timezone, ...COMMON_TIMEZONES ]

  return (
    <div className="text-muted text-[13px] mt-1 flex items-center gap-1.5 flex-wrap">
      <span>🕐 {t('people.local_time', { time: localTimeIn(person.timezone) })}</span>
      {canWrite ? (
        <select
          value={person.timezone}
          onChange={(e) => router.patch(`/people/${person.id}`, { timezone: e.target.value })}
          className="border border-line rounded-lg px-1.5 py-0.5 text-[12px] outline-none focus:border-brand bg-card"
          title={t('people.timezone_title')}
        >
          {options.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      ) : (
        <span>· {person.timezone}</span>
      )}
    </div>
  )
}

// label 写成惰性字面量 t()：模块级直接求值会赶在 initCatalog() 之前（目录还是空的），
// 而写成 t(key) 的间接形式会让 i18n_client_keys_test 的静态扫描误判成死 key。
const ACCESS_BADGE: Record<AccessLevel, { label: () => string; cls: string }> = {
  owner: { label: () => t('people.access.owner'), cls: 'bg-tint text-brand' },
  write: { label: () => t('people.access.write'), cls: 'bg-green-tint text-green-strong' },
  // 给孩子的最小权限：能申报「我做完了」，但不能直接加分、不能审核自己的申报
  claim: { label: () => t('people.access.claim'), cls: 'bg-amber-tint text-amber-strong' },
  read: { label: () => t('people.access.read'), cls: 'bg-amber-tint text-amber-strong' },
}

function ShareManager({ person }: { person: PersonRow }) {
  const [email, setEmail] = useState('')
  const [access, setAccess] = useState<ShareAccess>('read')

  const share = () => {
    if (!email.trim()) return
    router.post(`/people/${person.id}/share`, { email: email.trim(), access }, { onSuccess: () => setEmail('') })
  }
  const unshare = (userId: number) =>
    router.delete(`/people/${person.id}/unshare`, { data: { user_id: userId } })

  return (
    <div className="mt-2 pl-3 border-l-2 border-tint">
      {person.shares && person.shares.length > 0 ? (
        <div className="grid gap-1.5">
          {person.shares.map((s) => (
            <div key={s.user_id} className="flex items-center gap-2 text-[13px]">
              <span className="text-muted">🔗 {s.email}</span>
              <span className={'px-2 py-px rounded-md ' + ACCESS_BADGE[s.access].cls}>{ACCESS_BADGE[s.access].label()}</span>
              <button onClick={() => unshare(s.user_id)} className="text-muted hover:text-red">{t('people.unshare')}</button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-muted text-[13px]">{t('people.not_shared')}</div>
      )}
      <div className="flex gap-2 flex-wrap items-center mt-2">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('people.share_email')}
          className="flex-1 min-w-[150px] border border-line rounded-[10px] px-2.5 py-1.5 text-[13px] outline-none focus:border-brand"
        />
        <select
          value={access}
          onChange={(e) => setAccess(e.target.value as ShareAccess)}
          className="border border-line rounded-[10px] px-2 py-1.5 text-[13px] outline-none focus:border-brand"
        >
          <option value="read">{t('people.access.read')}</option>
          <option value="claim">{t('people.access.claim_option')}</option>
          <option value="write">{t('people.access.write')}</option>
        </select>
        <button onClick={share} className="bg-brand text-white font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">{t('people.share')}</button>
      </div>
    </div>
  )
}

// 三个破坏性 / 需输入的动作各走一个 sheet（规矩 §4「破坏性操作不与内容相邻」+
// §5「弹层走底部 sheet」）。系统 prompt / confirm 在手机上是居中弹窗、按钮小、
// 样式不受控，prompt 在部分 iOS 版本上甚至不给输入法。
//
// 共用一个 state 而不是三个布尔量：一次只可能开一个，三个布尔量会允许
// 「重命名和删除同时开着」这种不存在的状态。
type PersonAction = { kind: 'rename' | 'delete' | 'leave' | 'avatar'; person: PersonRow }

export default function Index({ people }: { people: PersonRow[] }) {
  const [name, setName] = useState('')
  const [action, setAction] = useState<PersonAction | null>(null)
  const [renameTo, setRenameTo] = useState('')
  const [uploading, setUploading] = useState(false)
  const closeAction = () => setAction(null)

  // 头像上传。压到 256px（最大只渲染到公开页的 64px），压不动就原样发 ——
  // 服务端那道 2MB + 白名单是安全边界，这里只是省上行流量。
  const pickAvatar = async (files: File[]) => {
    const p = action!.person
    const file = files[0]
    if (!file) return

    setUploading(true)
    const shrunk = await shrinkImage(file, 256)
    router.post(`/people/${p.id}/avatar`, { avatar: shrunk }, {
      forceFormData: true,
      onFinish: () => { setUploading(false); closeAction() },
    })
  }

  const removeAvatar = () => {
    const p = action!.person
    closeAction()
    router.delete(`/people/${p.id}/avatar`)
  }

  const add = () => { if (!name.trim()) return; router.post('/people', { name }, { onSuccess: () => setName('') }) }
  const askRename = (p: PersonRow) => { setRenameTo(p.name); setAction({ kind: 'rename', person: p }) }
  const doRename = () => {
    const p = action!.person
    closeAction()
    router.patch(`/people/${p.id}`, { name: renameTo.trim() })
  }
  const doDelete = () => {
    const p = action!.person
    closeAction()
    router.delete(`/people/${p.id}`)
  }
  const doLeave = () => {
    const p = action!.person
    closeAction()
    router.delete(`/people/${p.id}/leave`)
  }

  return (
    <Layout>
      <Card title={`👥 ${t('people.title')}`} extra={<span className="text-muted text-[13px]">{t('people.count', { count: people.length })}</span>}>
        {people.map((p) => {
          const canWrite = p.access === 'owner' || p.access === 'write'
          return (
            <div key={p.id} className="py-2.5 border-b border-dashed border-line last:border-0">
              <div className="flex items-center gap-2">
                {/* 头像本身就是入口（可写时）：手机上「点脸换脸」比再多一个 ✎ 图标
                    好找，而这一行右边已经有三个按钮了。只读时是纯展示，不给
                    点不动的按钮。 */}
                {/* self-start：这一行的文字块有三四行高（名字 + 汇总 + 时区），
                    居中的话头像会贴在「104 分…」旁边而不是名字旁边。 */}
                {canWrite ? (
                  <button
                    type="button"
                    onClick={() => setAction({ kind: 'avatar', person: p })}
                    aria-label={t('people.avatar_edit_aria', { name: p.name })}
                    className="shrink-0 self-start rounded-full active:opacity-70"
                  >
                    <Avatar name={p.name} url={p.avatar_url} size={36} />
                  </button>
                ) : (
                  <Avatar name={p.name} url={p.avatar_url} size={36} className="self-start" />
                )}
                {/* min-w-0：头像吃掉 44px 之后这一行在 375px 上会溢出（实测 36px，
                    「切换」被挤成两行、🗑 跑到卡片外）。flex-1 的 basis 是 0 但
                    min-width 默认 auto，不给 min-w-0 它就不肯让出宽度，收缩全
                    落到右边那几个按钮头上。 */}
                <span className="flex-1 min-w-0">
                  <b className={p.is_current ? 'font-extrabold' : 'font-semibold'}>{p.name}</b>{' '}
                  <span className={'text-[11px] px-[7px] py-px rounded-md ' + ACCESS_BADGE[p.access].cls}>{ACCESS_BADGE[p.access].label()}</span>
                  {p.is_current && <span className="text-[11px] px-[7px] py-px rounded-md bg-tint text-muted ml-1">{t('people.current')}</span>}
                  <div className="text-muted text-[13px]">
                    {t('people.summary', { balance: p.balance, streak: p.streak, total: p.total, goals: p.goals_count })}
                    {!p.is_owner && <> · {t('people.from', { email: p.owner_email })}</>}
                  </div>
                  <TimezonePicker person={p} canWrite={canWrite} />
                </span>
                {!p.is_current && <button onClick={() => router.post(`/people/${p.id}/switch`)} className="shrink-0 bg-tint text-brand font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">{t('people.switch')}</button>}
                {canWrite && <button onClick={() => askRename(p)} title={t('people.rename')} className="shrink-0 text-muted hover:text-brand text-base px-1.5">✎</button>}
                {p.is_owner && <button onClick={() => setAction({ kind: 'delete', person: p })} title={t('people.delete')} className="shrink-0 text-muted hover:text-red text-base px-1.5">🗑</button>}
                {!p.is_owner && <button onClick={() => setAction({ kind: 'leave', person: p })} title={t('people.leave')} className="shrink-0 text-muted hover:text-red text-[13px] px-1.5">{t('people.leave_short')}</button>}
              </div>
              {p.is_owner && <ShareManager person={p} />}
            </div>
          )
        })}
        <div className="flex gap-2.5 flex-wrap items-center mt-3.5">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('people.new_placeholder')} className="flex-1 min-w-[130px] border border-line rounded-[10px] px-2.5 py-2 outline-none focus:border-brand" />
          <button onClick={add} className="bg-brand text-white font-semibold px-4 py-2 rounded-[10px]">{t('people.add')}</button>
        </div>
        <div className="text-muted text-[13px] mt-2.5">
          {t('people.hint_owner')}
          <br />
          {t('people.hint_claim')}
          <br />
          {t('people.hint_delete')}
        </div>
      </Card>

      {/* 头像 sheet：复用作业页那两块砖（📷 拍照 / 🖼️ 相册），allowAudio 关掉。 */}
      <Sheet
        open={action?.kind === 'avatar'}
        onClose={closeAction}
        label={t('people.avatar')}
        title={action ? `${t('people.avatar')} · ${action.person.name}` : t('people.avatar')}
      >
        <SheetPad>
          <AttachmentPicker onPick={pickAvatar} busy={uploading} />
          <p className="m-0 mt-2.5 text-muted text-[13px] leading-[1.5]">{t('people.avatar_hint')}</p>
        </SheetPad>
        {action?.person.avatar_url && (
          <>
            <SheetDivider />
            <SheetRow icon="🗑" tone="danger" onClick={removeAvatar}>
              {t('people.avatar_remove')}
            </SheetRow>
          </>
        )}
      </Sheet>

      <Sheet open={action?.kind === 'rename'} onClose={closeAction} label={t('people.rename')} title={t('people.rename')}>
        <SheetPad>
          <Field label={t('people.rename_label')}>
            <input
              value={renameTo}
              onChange={(e) => setRenameTo(e.target.value)}
              enterKeyHint="done"
              autoComplete="off"
              className={fieldInputClass}
            />
          </Field>
          <Button disabled={!renameTo.trim()} onClick={doRename}>{t('people.rename')}</Button>
        </SheetPad>
      </Sheet>

      <Sheet
        open={action?.kind === 'delete'}
        onClose={closeAction}
        label={t('people.delete')}
        title={t('people.delete_title', { name: action?.person.name ?? '' })}
      >
        <div className="mx-5 mb-3.5 px-3.5 py-3 rounded-ctl bg-red-tint text-red-strong text-[13px] leading-[1.45]">
          {t('people.delete_warn')}
        </div>
        <SheetPad>
          <div className="grid gap-2">
            <Button variant="danger" onClick={doDelete}>{t('people.delete_confirm_btn')}</Button>
            <Button variant="plain" onClick={closeAction}>{t('people.keep')}</Button>
          </div>
        </SheetPad>
      </Sheet>

      <Sheet
        open={action?.kind === 'leave'}
        onClose={closeAction}
        label={t('people.leave')}
        title={t('people.leave_title', { name: action?.person.name ?? '' })}
      >
        <div className="mx-5 mb-3.5 px-3.5 py-3 rounded-ctl bg-amber-tint text-amber-strong text-[13px] leading-[1.45]">
          {t('people.leave_warn')}
        </div>
        <SheetPad>
          <div className="grid gap-2">
            <Button variant="danger" onClick={doLeave}>{t('people.leave')}</Button>
            <Button variant="plain" onClick={closeAction}>{t('people.keep')}</Button>
          </div>
        </SheetPad>
      </Sheet>
    </Layout>
  )
}
